import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { DataStream } from "src/app/shared/models/kpi.model";
import Chart from "chart.js/auto";
import numeral from "numeral";
import { CommonModule } from "@angular/common";
import { AuthService } from "src/app/shared/services/auth.service";

@Component({
  selector: "gen-kpi-radar",
  templateUrl: "./gen-kpi-radar.component.html",
  styleUrls: ["./gen-kpi-radar.component.scss"],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenKpiRadarComponent implements OnInit, OnDestroy {
  @Input() id: string = "";
  @Input() data: DataStream[];
  @Input() height: string;
  @Input() width: string;
  @Input() customTooltipId: string;
  @Input() showTooltip: boolean;
  @Input() showNumbers: boolean;

  canvas: any;
  ctx: any;
  myChart: any;
  chartId: string;
  tooltipDataPoints: any[] = [];

  constructor(private authService: AuthService, private cdr: ChangeDetectorRef) { }

  get isClinicOwner(): boolean {
    return this.authService.currentUser.roleCode == "CLINIC_OWNER";
  }

  ngOnInit() {
    this.chartId = "chart_" + this.id;
  }

  ngOnDestroy() {
    if (this.myChart) this.myChart.destroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.chartId) return;
    this.drawChart();
  }

  ngAfterViewInit() {
    if (!this.chartId) return;
    this.drawChart();
  }

  getDataSets(): Array<any> {
    let datasets = [];
    this.data
      .filter((d) => d.streamCode != "LABELS" && d.visible)
      .forEach((s) => {
        let dataset = {
          label: s.descr,
          data: s.values,
          tooltipData: s.values2,
          fill: true,
          borderColor: s.color,
          borderWidth: 1,
          backgroundColor: s.bgColor,
          pointBackgroundColor: s.bgColor,
          pointBorderColor: s.color,
          pointHoverBackgroundColor: s.bgColor,
          pointHoverBorderColor: s.color,
          type: s.type,
          code: s.streamCode,
        };
        datasets.push(dataset);
      });
    return datasets;
  }

  drawChart() {
    if (!this.chartId) return;
    if (this.myChart) this.myChart.destroy();

    this.canvas = document.getElementById(this.chartId);
    if (!this.canvas) return;

    this.ctx = this.canvas.getContext("2d");
    Chart.defaults.font = {
      family: "Rubik",
      size: 14,
    };
    this.myChart = new Chart(this.ctx, {
      type: "radar",
      data: {
        labels: this.data.filter((d) => d.streamCode == "LABELS")[0].labels,
        datasets: this.getDataSets(),
      },
      options: {
        elements: {
          line: {
            borderWidth: 3,
          },
        },
        scales: {
          r: {
            angleLines: {
              display: false,
            },
            ticks: {
              display: this.showNumbers,
              callback: (value) => {
                return numeral(value).format("0.0a");
              },
            },
            pointLabels: {
              font: {
                size: 12,
              },
            },
          },
        },
        maintainAspectRatio: false,
        animation: {
          duration: 0,
        },
        layout: {
          padding: {
            top: 10,
          },
        },

        plugins: {
          legend: {
            display: true,
            position: "top",
            align: "center",
            rtl: true,
            labels: {
              usePointStyle: false,
              boxWidth: 10,
              boxHeight: 10,
              font: {
                size: 12,
              },
              // boxPadding: 10,
              padding: 10,
            },
          },
          tooltip: {
            enabled: !this.customTooltipId && this.showTooltip,
            position: "nearest",
            external:
              this.customTooltipId && this.showTooltip
                ? this.externalTooltipHandler
                : null,
            usePointStyle: true,
            displayColors: false,
            backgroundColor: "rgb(235, 238, 242)",
            bodyColor: "black",
            titleColor: "black",
            rtl: true,
            padding: 6,
          },
        },
      },
    });
  }

  getOrCreateTooltip = (chart) => {
    let tooltipEl = chart.canvas.parentNode.querySelector("div");

    if (!tooltipEl) {
      tooltipEl = document.getElementById(this.customTooltipId);
      tooltipEl.style.opacity = 1;
      tooltipEl.style.pointerEvents = "none";
      tooltipEl.style.position = "absolute";
      tooltipEl.style.transform = "translate(-50%, 0)";
      tooltipEl.style.transition = "all .1s ease";
      chart.canvas.parentNode.appendChild(tooltipEl);
    }

    return tooltipEl;
  };

  externalTooltipHandler = (context) => {
    // Tooltip Element
    const { chart, tooltip } = context;
    const tooltipEl = this.getOrCreateTooltip(chart);

    // Hide if no tooltip
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }
    var tooltipModel = context.tooltip;

    //this.tooltipYLabel = tooltipModel.title[0];
    this.tooltipDataPoints = tooltip.dataPoints;

    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

    // Display, position, and set styles for font
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = positionX + tooltip.caretX + "px";
    tooltipEl.style.top = positionY + tooltip.caretY + 15 + "px";
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding =
      tooltip.options.padding + "px " + tooltip.options.padding + "px";
    this.cdr.markForCheck();
  };

  getTooltipData(point: any) {
    return point.dataset.tooltipData[point.dataIndex];
  }
}
